import { Component, EventEmitter, inject, Input, Output, Signal, signal, WritableSignal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { OrganizationFacade } from '../../../../shared/state/organization/facades/organization.facade';
import { IOrganization } from '../../../../shared/models/organization';
import { SortableDirective } from '../../../../shared/components/directives/sorting/user-list-sort';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

@Component({
  selector: 'app-organization-list',
  templateUrl: './organization-list.component.html',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, SortableDirective, FontAwesomeModule],
})
export class OrganizationListComponent {
  private organizationFacade = inject(OrganizationFacade);
  private fb = inject(FormBuilder);

  @Input() activeFilter: string = 'all';
  @Output() organizationSelected = new EventEmitter<number>();
  @Output() configureGuidelines = new EventEmitter<IOrganization>();

  currentSortColumn: string = '';
  currentSortDirection: 'asc' | 'desc' = 'asc';
  organizationListSignal: Signal<IOrganization[]> = this.organizationFacade.organizationListSignal;

  sortedOrganizations: WritableSignal<IOrganization[]> = signal([]);

  // Edit state
  editingId: number | null = null;
  editForm: FormGroup = this.fb.group({
    display_name: ['', [Validators.required, Validators.minLength(1)]],
  });

  constructor() {
    effect(() => {
      const organizations = this.organizationListSignal();
      if (organizations && Array.isArray(organizations)) {
        this.sortedOrganizations.set([...organizations]);
      }
    });
  }

  onSort({ column, direction }: { column: string; direction: 'asc' | 'desc' }) {
    this.currentSortColumn = column;
    this.currentSortDirection = direction;

    if (!direction) {
      this.sortedOrganizations.set([...this.organizationListSignal()] as IOrganization[]);
      return;
    }

    const organizations = this.organizationListSignal();
    if (!organizations || organizations.length === 0) return;

    const sorted = [...organizations].sort((a, b) => this.compareValues(a, b));
    this.sortedOrganizations.set(sorted);
  }

  private compareValues(a: IOrganization, b: IOrganization) {
    if (!this.currentSortColumn) return 0;

    const column = this.currentSortColumn;
    let valueA: string;
    let valueB: string;

    if (column === 'display_name') {
      valueA = a.display_name ?? '';
      valueB = b.display_name ?? '';
    } else {
      valueA = String(a[column as keyof IOrganization] ?? '');
      valueB = String(b[column as keyof IOrganization] ?? '');
    }

    return this.currentSortDirection === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
  }

  viewOrganization(organizationId: number) {
    this.organizationSelected.emit(organizationId);
  }

  onToggleDefaultApprove(organization: IOrganization) {
    const patch = {
      default_approve_analytical_dataset_requests: !(organization.default_approve_analytical_dataset_requests ?? false),
    };
    this.organizationFacade.patchOrganization(organization.id, patch);
  }

  onDelete(organization: IOrganization) {
    this.organizationSelected.emit(organization.id);
  }

  onConfigureGuidelines(organization: IOrganization) {
    this.configureGuidelines.emit(organization);
  }

  beginEdit(organization: IOrganization) {
    this.editingId = organization.id;
    this.editForm.patchValue({ display_name: organization.display_name });
  }

  saveEdit() {
    if (this.editForm.invalid || this.editingId == null) return;
    const patch = { display_name: this.editForm.value.display_name };
    this.organizationFacade.patchOrganization(this.editingId, patch);
    this.editingId = null;
    this.editForm.reset();
  }

  cancelEdit() {
    this.editingId = null;
    this.editForm.reset();
  }
}
